import { Environment } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { EffectComposer, Noise, Vignette } from "@react-three/postprocessing";
import { Leva } from 'leva';
import React, { Suspense, useEffect, useRef, useState } from "react";
import { LUTCubeLoader } from 'three/examples/jsm/loaders/LUTCubeLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import Loader from "./components/Loader";
import { sections } from "./data/sections";
import DotNavigation from "./DotNavigation";
import CameraWithAnimation from "./Navigation";
import Outside from "./Outside";
import SectionTitle from "./SectionTitle";

// Preload the HDR environment map
const rgbeLoader = new RGBELoader();
rgbeLoader.load('/kloofendal_43d_clear_puresky_4k.hdr');

// Mobile detection
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const styles = {
  startButton: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: isMobile ? '1rem 2rem' : '1.5rem 3.5rem',
    fontSize: isMobile ? 'clamp(0.8rem, 2vw, 1.2rem)' : 'clamp(1rem, 1.5vw, 1.4rem)',
    backgroundColor: 'transparent',
    color: '#ffffff',
    border: '2px solid rgba(255, 255, 255, 0.9)',
    borderRadius: '50px',
    cursor: 'pointer',
    letterSpacing: '0.3em',
    fontFamily: 'serif',
    fontWeight: '300',
    textTransform: 'uppercase',
    transition: 'all 0.4s ease',
    zIndex: 22332224,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    }
  },
  canvasContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    pointerEvents: 'none',
    zIndex: 10
  },
  canvasVisible: {
    opacity: 1,
    pointerEvents: 'auto'
  }
};

export default function Experience() {
  const [isSceneLoaded, setIsSceneLoaded] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [showStartButton, setShowStartButton] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [lutTexture, setLutTexture] = useState(null);
  const [lutError, setLutError] = useState(null);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const loaderTimeoutRef = useRef(null);
  const isTransitioning = useRef(false);
  const scrollTimeout = useRef(null);
  const lastScrollTime = useRef(Date.now());
  const touchStart = useRef(null);

  useEffect(() => {
    const loader = new LUTCubeLoader();
    
    const loadLUT = () => {
      loader.load(
        '/lut/lut.cube',
        (texture) => {
          if (texture && texture.image) {
            texture.needsUpdate = true;
            setLutTexture(texture);
          } else {
            setLutError('Invalid texture loaded');
          }
        },
        undefined,
        () => {
          setLutError('Failed to load LUT texture');
        }
      );
    };

    loadLUT();
  }, []);

  const handleLoaderFinish = () => {
    setIsSceneLoaded(true);
    setIsCanvasReady(true);

    // Add a delay to match the loader's timing
    setTimeout(() => {
      setShowStartButton(true);
      // Add a small delay before setting isSceneLoaded to true to ensure smooth fade-in
      setTimeout(() => {
        setIsSceneLoaded(true);
      }, 100);
    }, isMobile ? 2000 : 6500); // 1 second for mobile, 6.5 seconds for desktop
  };

  const handleStartExperience = () => {
    // First fade out the button
    const button = document.querySelector('.start-button');
    if (button) {
      button.style.opacity = '0';
      button.style.transform = 'translate(-50%, -30%)';
      
      // Wait for fade out animation to complete before starting the experience
      setTimeout(() => {
        setHasStarted(true);
        setShowStartButton(false);
      }, 1200); // Match the transition duration
    } else {
      // Fallback if button element not found
      setHasStarted(true);
      setShowStartButton(false);
    }
  };

  const handleSectionChange = (sectionIndex) => {
    if (isTransitioning.current) return;
    
    isTransitioning.current = true;
    setCurrentSection(sectionIndex);
    
    setTimeout(() => {
      isTransitioning.current = false;
    }, 1500);
  };

  const handleScroll = (event) => {
    if (!hasStarted || isTransitioning.current) {
      return;
    }

    const now = Date.now();
    if (now - lastScrollTime.current < 500) return;
    lastScrollTime.current = now;

    const direction = Math.sign(event.deltaY);
    let nextSection = currentSection + direction;

    // Create infinite loop starting from section 1 (after INITIAL)
    if (nextSection < 1) {
      nextSection = sections.length - 1; // Go to last section when scrolling up from first non-INITIAL section
    } else if (nextSection >= sections.length) {
      nextSection = 1; // Go to first non-INITIAL section when scrolling down from last
    }

    if (nextSection !== currentSection) {
      handleSectionChange(nextSection);
    }
  };

  const handleTouchStart = (e) => {
    touchStart.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (!touchStart.current || isTransitioning.current) return;

    const touchEnd = e.touches[0].clientY;
    const delta = touchStart.current - touchEnd;

    if (Math.abs(delta) > 50) {
      const direction = Math.sign(delta);
      let nextSection = currentSection + direction;

      // Create infinite loop starting from section 1 (after INITIAL)
      if (nextSection < 1) {
        nextSection = sections.length - 1;
      } else if (nextSection >= sections.length) {
        nextSection = 1;
      }

      if (nextSection !== currentSection) {
        handleSectionChange(nextSection);
        touchStart.current = null;
      }
    }
  };

  useEffect(() => {
    if (hasStarted) {
      window.addEventListener('wheel', handleScroll, { passive: true });
      window.addEventListener('touchstart', handleTouchStart);
      window.addEventListener('touchmove', handleTouchMove);
    }
    return () => {
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [hasStarted, currentSection]);

  useEffect(() => {
    return () => {
      if (loaderTimeoutRef.current) clearTimeout(loaderTimeoutRef.current);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, []);

  return (
    <>
      <Leva />
      <div style={{
        ...styles.canvasContainer,
        ...(isCanvasReady ? styles.canvasVisible : {})
      }}>
        <Suspense fallback={null}>
          <Canvas
            gl={{ 
              antialias: true,
              powerPreference: isMobile ? "low-power" : "high-performance",
              precision: isMobile ? "mediump" : "highp",
              stencil: false,
              depth: true,
              alpha: false,
              logarithmicDepthBuffer: false
            }}
            dpr={isMobile ? [1, 1.5] : [1, 1.5]}
            camera={{
              fov: isMobile ? 45 : 30,
              near: 0.1,
              far: 1000,
              position: [11.3, 34.3, -10.3]
            }}
            performance={{
              min: 0.5,
              max: 1,
              current: 1,
              debounce: 200
            }}
          >
            <CameraWithAnimation 
              hasStarted={hasStarted}
              sections={sections}
              currentSection={currentSection}
              onSectionChange={handleSectionChange}
            />
            <Environment
              files={isMobile ? "/kloofendal_43d_clear_puresky_1k.hdr" : "/kloofendal_43d_clear_puresky_4k.hdr"} 
              background 
            />
            <Outside />
            <EffectComposer multisampling={0} disableNormals>
              <Noise opacity={0.02} />
              <Vignette
                offset={0.5} 
                darkness={0.5} 
                eskil={false} 
              />
            </EffectComposer>
          </Canvas>
        </Suspense>
      </div>

      {showLoader && <Loader onLoaderFinish={handleLoaderFinish} />}
      
      {showStartButton && !hasStarted && (
        <button 
          onClick={handleStartExperience}
          className="start-button"
          style={{
            ...styles.startButton,
            opacity: isSceneLoaded ? 1 : 0,
            transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isSceneLoaded ? 'translate(-50%, -50%)' : 'translate(-50%, -30%)',
            visibility: showStartButton ? 'visible' : 'hidden',
          }}
        >
          ENTER THE EXPERIENCE
        </button>
      )}

      {hasStarted && (
        <>
          <SectionTitle 
            sections={sections}
            currentSection={currentSection}
          />
          <DotNavigation 
            sections={sections}
            currentSection={currentSection}
            onSectionChange={handleSectionChange}
          />
        </>
      )}
    </>
  );
}
